import React, { useMemo } from 'react';

import {
  Grid,
} from '@mui/material';
import dayjs from 'dayjs';

import ContentCard from '../../../components/ContentCard';
import SkeletonStack from '../../../components/SkeletonStack';
import { useGetCoursesQuery } from '../../../services/courses';
import { useGetEnrolmentSnapshotsQuery } from '../../../services/snapshots';
import { CourseEnrolmentState } from '../../../types/common';
import addDays from '../../../utils/common/addDays';
import useError from '../../../utils/react/hooks/useError';
import { EnrolmentCountByGradYearAndStateOverTimeBarChart, EnrolmentCountYoYOverTimeLineChart } from '../ChartComponents';
import { CountEntry } from '../types';
import { getDateAttributes, getEnrolmentCountAttributes } from '../utils';

import {
  getCountAttributes,
  getEmptyCountsBySubjectAndCourse,
  getYearAsString,
  mergeDuplicateAttributesBySum,
} from './utils';

const END_OF_DAY = dayjs().endOf('day');

interface EnrolmentsTabProps {
  subject: string;
}

const EnrolmentsTab = ({ subject }: EnrolmentsTabProps) => {
  const {
    data: enrolmentSnapshots,
    isLoading: isEnrolmentSnapshotsLoading,
    isError: isEnrolmentSnapshotsError,
    error: enrolmentSnapshotsError,
  } = useGetEnrolmentSnapshotsQuery({
    startDatetime: 0,
    endDatetime: addDays(END_OF_DAY, -1).valueOf(),
  });

  const {
    data: courses,
    isLoading: isCoursesLoading,
    isError: isCoursesError,
    error: coursesError,
  } = useGetCoursesQuery({});

  const enrolmentCountBySubjectAndCourseOverTime = useMemo(() => {
    if (enrolmentSnapshots?.data && courses) {
      // Empty array for each course and subject
      const emptyCounts = getEmptyCountsBySubjectAndCourse(courses.data);

      // Iterate through snapshots to count enrolments
      enrolmentSnapshots.data.forEach((snapshot) => {
        const { countByGradYearThenState } = snapshot;

        Object.entries(countByGradYearThenState).forEach(([course, snapshotCounts]) => {
          const dateAttributes = getDateAttributes(snapshot.datetime);
          const countAttributes = getEnrolmentCountAttributes(snapshotCounts);

          // Write counts for course
          emptyCounts[course].counts.push({
            ...dateAttributes,
            ...countAttributes,
          } as CountEntry);

          // Write counts for subject
          const subjectOfCourse = course.split('#')[0];

          // Find existing entry for the same datetime to tally
          const existingEntryIndex = emptyCounts[subjectOfCourse].counts
            .findIndex((entry) => entry.datetime === snapshot.datetime);
          const existingEntry = emptyCounts[subjectOfCourse].counts[existingEntryIndex];

          // Remove the original entry if it exists
          if (existingEntryIndex !== -1) {
            emptyCounts[subjectOfCourse].counts.splice(existingEntryIndex, 1);
          }

          // Write the new entry
          const existingCountAttributes = getCountAttributes(existingEntry);
          emptyCounts[subjectOfCourse].counts.push({
            ...dateAttributes,
            ...mergeDuplicateAttributesBySum(existingCountAttributes, countAttributes),
          } as CountEntry);

          // Store plottable categories based on the available keys
          // This is necessary for recharts
          Object.keys(countAttributes).forEach((x) => {
            emptyCounts[course].plottableCategories.add(x);
            emptyCounts[subjectOfCourse].plottableCategories.add(x);
          },
          );
        });
      });

      return emptyCounts;
    }

    return undefined;
  }, [enrolmentSnapshots, courses]);

  const enrolmentCountBySubjectAndCourseYoY = useMemo(() => {
    if (enrolmentCountBySubjectAndCourseOverTime && courses) {
      const emptyCounts = getEmptyCountsBySubjectAndCourse(courses.data);

      Object.entries(enrolmentCountBySubjectAndCourseOverTime)
        .forEach(([subjectOrCourse, details]) => {
          const sortedCounts = details.counts
            .sort((a, b) => a.datetime - b.datetime);
          console.log('sortedCounts');
          console.log(sortedCounts);

          sortedCounts.forEach((count) => {
            // Find existing entry for the same display date to tally
            const existingEntryIndex = emptyCounts[subjectOrCourse].counts
              .findIndex((entry) => entry.displayDate === count.displayDate);
            const existingEntry = emptyCounts[subjectOrCourse].counts[existingEntryIndex];

            // Remove the original entry if it exists
            if (existingEntryIndex !== -1) {
              emptyCounts[subjectOrCourse].counts.splice(existingEntryIndex, 1);
            }

            // Write the new entry
            const year = getYearAsString(count?.datetime);
            const existingCountAttributes = getCountAttributes(existingEntry);
            const newCountEntry = {
              ...existingCountAttributes,
              displayDate: count.displayDate,
              [year]: count.total,
            };
            emptyCounts[subjectOrCourse].counts.push(newCountEntry as CountEntry);

            // Store plottable categories based on the available keys
            // This is necessary for recharts
            [
              ...Object.keys(existingCountAttributes),
              year,
            ].forEach((x) => {
              emptyCounts[subjectOrCourse].plottableCategories.add(x);
            });
          });
        });

      return emptyCounts;
    }

    return undefined;
  }, [enrolmentCountBySubjectAndCourseOverTime, courses]);

  useError(isEnrolmentSnapshotsError, enrolmentSnapshotsError);
  useError(isCoursesError, coursesError);

  if (enrolmentCountBySubjectAndCourseYoY?.['chem#chem12']) {
    console.log(enrolmentCountBySubjectAndCourseYoY['chem#chem12'].counts);
  }

  if (isEnrolmentSnapshotsLoading
    || isCoursesLoading
    || !enrolmentCountBySubjectAndCourseOverTime
    || !enrolmentCountBySubjectAndCourseYoY
    || !courses) {
    return <SkeletonStack />;
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ContentCard title="Subject Enrolments" variant="outlined">
          <EnrolmentCountByGradYearAndStateOverTimeBarChart
            data={enrolmentCountBySubjectAndCourseOverTime[subject].counts}
            // Plot the graduation years with the states specified
            plottableGradYearStates={Array.from(
              enrolmentCountBySubjectAndCourseOverTime[subject].plottableCategories,
            ).filter((gradYearState) =>
              gradYearState.includes(CourseEnrolmentState.Active)
              || gradYearState.includes(CourseEnrolmentState.OnTrial),
            )}
          />
        </ContentCard>
      </Grid>
      {courses.data.filter((course) => course.subject === subject)
        .sort((a, b) => a.courseId.localeCompare(b.courseId))
        .map((course) => {
          const subjectCourse = `${course.subject}#${course.courseId}`;

          return (
            <Grid item xs={6} key={course.id}>
              <ContentCard title={`YoY ${course.label} Enrolments`} variant="outlined">
                <EnrolmentCountYoYOverTimeLineChart
                  data={enrolmentCountBySubjectAndCourseYoY[subjectCourse].counts}
                  plottableSampleYears={Array.from(
                    enrolmentCountBySubjectAndCourseYoY[subjectCourse].plottableCategories,
                  )}
                  xAxisDataKey="displayDate"
                />
              </ContentCard>
            </Grid>
          );
        })}
    </Grid>
  );
};
export default EnrolmentsTab;
